import * as React from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";

interface ResponsiveDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

interface ResponsiveDialogContentProps {
  children: React.ReactNode;
  className?: string;
}

interface ResponsiveDialogHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface ResponsiveDialogTitleProps {
  children: React.ReactNode;
  className?: string;
}

interface ResponsiveDialogDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

interface ResponsiveDialogTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

interface ResponsiveDialogFooterProps {
  children: React.ReactNode;
  className?: string;
}

interface ResponsiveDialogBodyProps {
  children: React.ReactNode;
  className?: string;
}

const ResponsiveDialogContext = React.createContext<{ isMobile: boolean }>({
  isMobile: false,
});

const useResponsiveDialog = () => React.useContext(ResponsiveDialogContext);

const ResponsiveDialog = ({ open, onOpenChange, children }: ResponsiveDialogProps) => {
  const isMobile = useIsMobile();

  return (
    <ResponsiveDialogContext.Provider value={{ isMobile }}>
      {isMobile ? (
        <Drawer open={open} onOpenChange={onOpenChange}>
          {children}
        </Drawer>
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          {children}
        </Dialog>
      )}
    </ResponsiveDialogContext.Provider>
  );
};

const ResponsiveDialogTrigger = ({ children, asChild }: ResponsiveDialogTriggerProps) => {
  const { isMobile } = useResponsiveDialog();

  if (isMobile) {
    return <DrawerTrigger asChild={asChild}>{children}</DrawerTrigger>;
  }
  return <DialogTrigger asChild={asChild}>{children}</DialogTrigger>;
};

const ResponsiveDialogContent = ({ children, className }: ResponsiveDialogContentProps) => {
  const { isMobile } = useResponsiveDialog();

  if (isMobile) {
    return (
      <DrawerContent className={cn("px-4 pb-0", className)}>
        <div className="flex flex-col max-h-[90vh]">
          {children}
        </div>
      </DrawerContent>
    );
  }

  return (
    <DialogContent className={cn("max-w-2xl max-h-[90vh] overflow-y-auto", className)}>
      {children}
    </DialogContent>
  );
};

const ResponsiveDialogBody = ({ children, className }: ResponsiveDialogBodyProps) => {
  const { isMobile } = useResponsiveDialog();

  if (isMobile) {
    return (
      <div className={cn("flex-1 overflow-y-auto pt-2 pb-4", className)}>
        {children}
      </div>
    );
  }

  return <div className={className}>{children}</div>;
};

const ResponsiveDialogHeader = ({ children, className }: ResponsiveDialogHeaderProps) => {
  const { isMobile } = useResponsiveDialog();

  if (isMobile) {
    return <DrawerHeader className={cn("text-left px-0 shrink-0", className)}>{children}</DrawerHeader>;
  }
  return <DialogHeader className={className}>{children}</DialogHeader>;
};

const ResponsiveDialogTitle = ({ children, className }: ResponsiveDialogTitleProps) => {
  const { isMobile } = useResponsiveDialog();

  if (isMobile) {
    return (
      <DrawerTitle className={cn("text-xl font-playfair", className)}>
        {children}
      </DrawerTitle>
    );
  }
  return (
    <DialogTitle className={cn("text-2xl font-playfair", className)}>
      {children}
    </DialogTitle>
  );
};

const ResponsiveDialogDescription = ({ children, className }: ResponsiveDialogDescriptionProps) => {
  const { isMobile } = useResponsiveDialog();

  if (isMobile) {
    return <DrawerDescription className={className}>{children}</DrawerDescription>;
  }
  return <DialogDescription className={className}>{children}</DialogDescription>;
};

const ResponsiveDialogFooter = ({ children, className }: ResponsiveDialogFooterProps) => {
  const { isMobile } = useResponsiveDialog();

  if (isMobile) {
    return (
      <div className={cn(
        "shrink-0 border-t bg-background px-0 py-4 -mx-4 px-4",
        className
      )}>
        {children}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col sm:flex-row justify-end gap-3 pt-4", className)}>
      {children}
    </div>
  );
};

export {
  ResponsiveDialog,
  ResponsiveDialogTrigger,
  ResponsiveDialogContent,
  ResponsiveDialogBody,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  useResponsiveDialog,
};
